import {makeAutoObservable} from 'mobx';
import {CustomerPortal, CustomerPortalQuote} from '../../types';
import {RequestReaction} from '../../../Core/mobx/types';
import RootStore from '../../../Core/mobx/RootStore';
import CustomerPortalAPI from '../../../APIs/CustomerPortalAPI';

export default class CustomerPortalStore {
    dialogOpen: boolean = false;
    customers: CustomerPortal[] | null = null;
    selectedCustomer: CustomerPortal | null = null;
    previousSelectedCustomer: CustomerPortal | null = null;
    search: string = '';

    constructor() {
        makeAutoObservable(this);
    }

    setSelectedCustomer(customer: CustomerPortal | null) {
        this.previousSelectedCustomer = this.selectedCustomer ? Object.assign({}, this.selectedCustomer) : customer;
        this.selectedCustomer = customer;
        this.search = '';
        if (this.selectedCustomer) {
            this.customers = [this.selectedCustomer];
        }
    }

    resetSelectedCustomer() {
        if (this.previousSelectedCustomer) {
            this.customers = [this.previousSelectedCustomer];
            this.selectedCustomer = this.previousSelectedCustomer;
            this.search = '';
        }
    }

    get isCustomerSelectionChanged() {
        if (!this.previousSelectedCustomer || !this.selectedCustomer) {
            return false;
        }
        return this.previousSelectedCustomer?.email !== this.selectedCustomer?.email;
    }

    async getCustomers(search: string) {
        const customerResponse = await CustomerPortalAPI.searchCustomers(search);

        if (customerResponse && customerResponse.data && customerResponse.data.searchCustomers) {
            this.customers = customerResponse.data.searchCustomers;
        }
    }

    async getCustomerById(id: string) {
        try {
            const resp = await CustomerPortalAPI.GetCustomerById(id);
            if (!resp || resp.error || resp.errors) {
                const error = new Error('There was an error fetching the customer by id');
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (error as any).response = resp;
                throw error;
            }
            return resp.customerById;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error(`Could not get customer for id: ${id}`, e);
            return null;
        }
    }

    async selectCustomerById(id: string) {
        const customer = await this.getCustomerById(id);

        if (!customer) return null;

        this.setSelectedCustomer(customer);

        return customer;
    }

    async createCustomerAndQuote(customer: CustomerPortal, quote: CustomerPortalQuote, {next}: RequestReaction) {
        const customerCreateResp = await CustomerPortalAPI.createCustomer(customer);
        if (customerCreateResp) {
            if (!customerCreateResp.errors && customerCreateResp.data.createCustomer?.id) {
                this.getCustomers(customerCreateResp.data.createCustomer.email);
                this.setSelectedCustomer(customerCreateResp.data.createCustomer);
                const quoteCreateResp = await CustomerPortalAPI.createQuote(quote);
                if (quoteCreateResp && !quoteCreateResp.errors) {
                    await RootStore.agentPortalStore.contactsFormStore.saveForm();
                    this.setSelectedCustomer(this.selectedCustomer);
                    this.dialogOpen = false;
                    next?.({customer: customerCreateResp, quote: quoteCreateResp});
                    return;
                } else {
                    // eslint-disable-next-line no-console
                    console.error('Quote creation is having errors :: ', quoteCreateResp?.errors);
                }
            } else {
                // eslint-disable-next-line no-console
                console.error('Customer creating is having errors :: ', customerCreateResp.errors);
            }
        }
        next?.({customer: {errors: 'failed to create customer'}, quote: {errors: 'failed to create quote'}});
    }
}
